import { useState, useRef, useCallback, type KeyboardEvent } from 'react';
import { ArrowUp, Square } from 'lucide-react';
import styles from './ChatInput.module.css';

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  /** `compact`: the slim field of the right panel. */
  variant?: "inline" | "hero" | "compact";
}

export function ChatInput({
  onSend,
  onStop,
  disabled = false,
  isStreaming = false,
  placeholder = 'Ask anything…',
  autoFocus = false,
  variant = "inline",
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (isStreaming) {
          onStop?.();
        } else {
          handleSend();
        }
      }
    },
    [handleSend, isStreaming, onStop]
  );

  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }
  }, []);

  const hasContent = value.trim().length > 0;
  const canSend = hasContent && !disabled;
  const isHero = variant === "hero";
  const isCompact = variant === "compact";
  const areaClassName = [
    styles.inputArea,
    isHero ? styles.inputAreaHero : "",
    isCompact ? styles.inputAreaCompact : "",
  ]
    .filter(Boolean)
    .join(" ");
  const wrapperClassName = [
    styles.inputWrapper,
    isHero ? styles.inputWrapperHero : "",
    isCompact ? styles.inputWrapperCompact : "",
    // Mark the wrapper as "active" the moment the user has typed
    // something — matches the dashboard chat composer, where the pill
    // visually transitions from quiet at-rest to ready-to-send.
    hasContent ? styles.inputWrapperActive : "",
  ]
    .filter(Boolean)
    .join(" ");
  const textareaClassName = [
    styles.textarea,
    isHero ? styles.textareaHero : "",
  ]
    .filter(Boolean)
    .join(" ");
  const sendButtonClassName = [
    styles.sendButton,
    isHero ? styles.sendButtonHero : "",
    isCompact ? styles.sendButtonCompact : "",
    isStreaming ? styles.stopButton : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={areaClassName}>
      <div className={wrapperClassName}>
        <textarea
          ref={textareaRef}
          className={textareaClassName}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={placeholder}
          rows={1}
          // Enter sends, so the return key of a touch keyboard says Send.
          enterKeyHint="send"
          disabled={disabled && !isStreaming}
          autoFocus={autoFocus}
        />
        <div className={styles.inputActions}>
          {isStreaming ? (
            <button
              className={sendButtonClassName}
              onClick={onStop}
              title="Stop generating"
              type="button"
              aria-label="Stop generating"
            >
              <Square className={styles.stopIcon} strokeWidth={0} />
            </button>
          ) : (
            <button
              className={sendButtonClassName}
              onClick={handleSend}
              disabled={!canSend}
              title="Send message"
              type="button"
              aria-label="Send message"
            >
              <ArrowUp className={styles.sendIcon} strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
